import { useState, useEffect, useCallback, useRef } from 'react';
import {
    calculateSheetOptimization, getOptimizerProducts,
    getGrainSettings, addGrainLockedDecor, removeGrainLockedDecor
} from '../lib/api';
import { useAuth } from '../lib/auth';
import {
    Plus, Trash, MagnifyingGlass, Calculator, ArrowsClockwise,
    WarningCircle, CheckCircle, Info, GearSix, Lock, LockOpen,
    CaretDown, CaretUp, ArrowCounterClockwise
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const DEFAULTS = {
    sheetLength: 2440,
    sheetWidth: 1220,
    edgeMargin: 5,
    cuttingGap: 6,
    tolerance: 10,
};

const COLORS = [
    '#C8A064', '#8BC34A', '#42A5F5', '#EF5350', '#AB47BC',
    '#FF7043', '#26A69A', '#5C6BC0', '#D4E157', '#EC407A',
    '#78909C', '#FFCA28', '#66BB6A', '#7E57C2', '#29B6F6',
];

function emptyRow() {
    return { id: crypto.randomUUID(), sku: '', decor: '', thickness: '', length: '', width: '', quantity: 1, orientation: 'auto' };
}

const ORIENTATION_OPTIONS = [
    { value: 'auto', label: 'Auto' },
    { value: 'landscape_only', label: 'Landscape' },
    { value: 'portrait_only', label: 'Portrait' },
    { value: 'flexible', label: 'Flexible' },
];

function ProductSearch({ onSelect }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const timerRef = useRef(null);

    const search = useCallback(async (q) => {
        if (!q || q.length < 2) { setResults([]); return; }
        try {
            const res = await getOptimizerProducts({ search: q });
            setResults(res.data.products || []);
            setOpen(true);
        } catch { /* ignore */ }
    }, []);

    const handleChange = (e) => {
        const v = e.target.value;
        setQuery(v);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => search(v), 300);
    };

    const select = (p) => {
        onSelect(p);
        setQuery('');
        setResults([]);
        setOpen(false);
    };

    return (
        <div className="relative">
            <div className="flex items-center gap-2">
                <MagnifyingGlass size={14} className="text-muted-foreground" />
                <Input
                    value={query}
                    onChange={handleChange}
                    onFocus={() => results.length > 0 && setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 200)}
                    placeholder="Search SKU/product to add..."
                    className="h-8 text-xs"
                    data-testid="optimizer-product-search"
                />
            </div>
            {open && results.length > 0 && (
                <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-card border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {results.map(p => (
                        <button
                            key={p.product_key}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-muted/50 transition-colors border-b border-border/30 last:border-0"
                            onMouseDown={() => select(p)}
                            data-testid={`search-result-${p.product_key}`}
                        >
                            <span className="font-mono font-semibold">{p.display_sku}</span>
                            <span className="text-muted-foreground ml-2">{p.product_name}</span>
                            <span className="text-muted-foreground ml-2">D{p.decor_no} | {p.thickness}</span>
                            {(p.length_mm && p.width_mm) && (
                                <span className="text-muted-foreground ml-2">{p.length_mm} x {p.width_mm} mm</span>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function SheetVisual({ sheet, sheetW, sheetH, skuColorMap }) {
    const scale = Math.min(480 / sheetW, 280 / sheetH);
    const svgW = sheetW * scale;
    const svgH = sheetH * scale;

    return (
        <div className="relative">
            <svg width={svgW} height={svgH} className="border border-border rounded bg-muted/30" data-testid={`sheet-visual-${sheet.sheet_number}`}>
                <rect x={0} y={0} width={svgW} height={svgH} fill="#f5f0e8" stroke="#C8A064" strokeWidth={1.5} rx={2} />
                {sheet.placements.map((p, i) => {
                    const x = p.x * scale;
                    const y = p.y * scale;
                    const w = p.w * scale;
                    const h = p.h * scale;
                    const color = skuColorMap[p.sku] || '#999';
                    return (
                        <g key={i}>
                            <rect x={x} y={y} width={w} height={h} fill={color} fillOpacity={0.25} stroke={color} strokeWidth={1.2} rx={1} />
                            {w > 28 && h > 14 && (
                                <text x={x + w / 2} y={y + h / 2} textAnchor="middle" dominantBaseline="central" fontSize={Math.min(10, w / 6, h / 3)} fill={color} fontWeight="600" fontFamily="monospace">
                                    {p.sku}
                                </text>
                            )}
                            {w > 40 && h > 24 && (
                                <text x={x + w / 2} y={y + h / 2 + 10} textAnchor="middle" dominantBaseline="central" fontSize={7} fill="#666" fontFamily="monospace">
                                    {Math.round(p.w)}x{Math.round(p.h)}{p.rotated ? ' R' : ''}
                                </text>
                            )}
                        </g>
                    );
                })}
            </svg>
            {/* Axis labels */}
            <div className="flex justify-between mt-1 px-1">
                <span className="text-[9px] text-muted-foreground">0</span>
                <span className="text-[9px] text-muted-foreground font-mono">Length: {sheetW} mm</span>
            </div>
            <div className="absolute -left-4 top-1/2 -translate-y-1/2 -rotate-90 origin-center">
                <span className="text-[9px] text-muted-foreground font-mono whitespace-nowrap">Width: {sheetH} mm</span>
            </div>
        </div>
    );
}

function AssumptionsPanel({ assumptions }) {
    if (!assumptions) return null;
    return (
        <div className="bg-muted/30 border border-border rounded-lg p-4" data-testid="assumptions-panel">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <Info size={14} />
                Settings Used for This Calculation
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                <div className="text-center">
                    <div className="text-xs text-muted-foreground">Sheet Size</div>
                    <div className="text-sm font-mono font-semibold">{assumptions.sheet_length_mm} x {assumptions.sheet_width_mm} mm</div>
                </div>
                <div className="text-center">
                    <div className="text-xs text-muted-foreground">Edge Margin</div>
                    <div className="text-sm font-mono font-semibold">{assumptions.edge_margin_mm} mm</div>
                </div>
                <div className="text-center">
                    <div className="text-xs text-muted-foreground">Cutting Gap</div>
                    <div className="text-sm font-mono font-semibold">{assumptions.cutting_gap_mm} mm</div>
                </div>
                <div className="text-center">
                    <div className="text-xs text-muted-foreground">Oversize Tolerance</div>
                    <div className="text-sm font-mono font-semibold">{assumptions.oversize_tolerance_mm} mm</div>
                </div>
                <div className="text-center">
                    <div className="text-xs text-muted-foreground">Rotation</div>
                    <div className="text-sm font-semibold">{assumptions.rotation_allowed ? 'Allowed' : 'Disabled'}</div>
                </div>
                <div className="text-center">
                    <div className="text-xs text-muted-foreground">Grain Lock</div>
                    <div className="text-sm font-semibold">
                        {assumptions.grain_lock_applied
                            ? `${assumptions.grain_locked_decors?.length || 0} decor(s)`
                            : 'None'}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function SheetOptimizer() {
    const { isAdmin } = useAuth();
    const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [allowOversize, setAllowOversize] = useState(false);
    const [allowOverprod, setAllowOverprod] = useState(false);
    const [grainSettings, setGrainSettings] = useState([]);
    const [grainDialogOpen, setGrainDialogOpen] = useState(false);
    const [newGrainDecor, setNewGrainDecor] = useState('');
    const [newGrainLabel, setNewGrainLabel] = useState('');
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Editable sheet settings
    const [sheetLength, setSheetLength] = useState(DEFAULTS.sheetLength);
    const [sheetWidth, setSheetWidth] = useState(DEFAULTS.sheetWidth);
    const [edgeMargin, setEdgeMargin] = useState(DEFAULTS.edgeMargin);
    const [cuttingGap, setCuttingGap] = useState(DEFAULTS.cuttingGap);
    const [tolerance, setTolerance] = useState(DEFAULTS.tolerance);

    const isDefaultSettings = sheetLength === DEFAULTS.sheetLength
        && sheetWidth === DEFAULTS.sheetWidth
        && edgeMargin === DEFAULTS.edgeMargin
        && cuttingGap === DEFAULTS.cuttingGap
        && tolerance === DEFAULTS.tolerance;

    useEffect(() => {
        getGrainSettings().then(res => setGrainSettings(res.data.grain_locked_decors || [])).catch(() => {});
    }, []);

    const handleAddGrain = async () => {
        if (!newGrainDecor.trim()) { toast.error('Enter a decor number'); return; }
        try {
            const res = await addGrainLockedDecor({ decor: newGrainDecor.trim(), label: newGrainLabel.trim() });
            setGrainSettings(prev => [...prev, res.data].sort((a, b) => a.decor.localeCompare(b.decor)));
            setNewGrainDecor('');
            setNewGrainLabel('');
            toast.success(`Decor ${res.data.decor} marked as grain-sensitive`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to add');
        }
    };

    const handleRemoveGrain = async (decor) => {
        try {
            await removeGrainLockedDecor(decor);
            setGrainSettings(prev => prev.filter(g => g.decor !== decor));
            toast.success(`Decor ${decor} removed from grain lock`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to remove');
        }
    };

    const resetSettings = () => {
        setSheetLength(DEFAULTS.sheetLength);
        setSheetWidth(DEFAULTS.sheetWidth);
        setEdgeMargin(DEFAULTS.edgeMargin);
        setCuttingGap(DEFAULTS.cuttingGap);
        setTolerance(DEFAULTS.tolerance);
    };

    const updateRow = (id, field, value) => {
        setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
    };

    const addRow = () => setRows(prev => [...prev, emptyRow()]);

    const removeRow = (id) => {
        setRows(prev => prev.length > 1 ? prev.filter(r => r.id !== id) : prev);
    };

    const handleProductSelect = (product) => {
        const emptyIdx = rows.findIndex(r => !r.sku);
        const dims = product.dimensions ? product.dimensions.replace(/mm/gi, '').trim().split(/\s*x\s*/i) : [];
        const length = product.length_mm || (dims[0] ? parseFloat(dims[0]) : '');
        const width = product.width_mm || (dims[1] ? parseFloat(dims[1]) : '');
        const thk = product.thickness || '';

        const newRow = {
            id: emptyIdx >= 0 ? rows[emptyIdx].id : crypto.randomUUID(),
            sku: product.display_sku || '',
            decor: product.decor_no || '',
            thickness: typeof thk === 'string' ? thk.replace(/\s*mm/i, '') : thk,
            length: length,
            width: width,
            quantity: 1,
            orientation: 'auto',
        };

        if (emptyIdx >= 0) {
            setRows(prev => prev.map((r, i) => i === emptyIdx ? newRow : r));
        } else {
            setRows(prev => [...prev, newRow]);
        }
    };

    const handleOptimize = async () => {
        const validRows = rows.filter(r => r.sku && r.length && r.width && r.quantity);
        if (validRows.length === 0) {
            toast.error('Add at least one product with dimensions and quantity');
            return;
        }

        setLoading(true);
        try {
            const res = await calculateSheetOptimization({
                items: validRows.map(r => ({
                    sku: r.sku,
                    decor: r.decor,
                    thickness: r.thickness,
                    length: parseFloat(r.length),
                    width: parseFloat(r.width),
                    quantity: parseInt(r.quantity) || 1,
                    orientation: r.orientation || 'auto',
                })),
                allow_oversize: allowOversize,
                allow_overproduction: allowOverprod,
                sheet_length_mm: parseFloat(sheetLength) || DEFAULTS.sheetLength,
                sheet_width_mm: parseFloat(sheetWidth) || DEFAULTS.sheetWidth,
                edge_margin_mm: parseFloat(edgeMargin) || 0,
                cutting_gap_mm: parseFloat(cuttingGap) || 0,
                oversize_tolerance_mm: parseFloat(tolerance) || 0,
            });
            setResults(res.data);
            toast.success('Optimization complete');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Optimization failed');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setRows([emptyRow(), emptyRow(), emptyRow()]);
        setResults(null);
    };

    // Build SKU->color map from results
    const skuColorMap = {};
    if (results) {
        const allSkus = new Set();
        results.groups.forEach(g => g.sheets.forEach(s => s.placements.forEach(p => allSkus.add(p.sku))));
        [...allSkus].forEach((sku, i) => { skuColorMap[sku] = COLORS[i % COLORS.length]; });
    }

    // Use response-driven dimensions for visuals
    const resultSheetL = results?.sheet_size?.length_mm || sheetLength;
    const resultSheetW = results?.sheet_size?.width_mm || sheetWidth;
    const oversizeApplied = results?.sheet_size?.oversize_applied;
    const effectiveTolerance = results?.parameters?.oversize_tolerance_mm || 0;
    const visW = resultSheetL + (oversizeApplied ? 2 * effectiveTolerance : 0);
    const visH = resultSheetW + (oversizeApplied ? 2 * effectiveTolerance : 0);

    return (
        <div className="space-y-6" data-testid="sheet-optimizer-page">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold tracking-tight">Sheet Optimization Calculator</h1>
                <p className="text-sm text-muted-foreground mt-1">Calculate minimum sheets required by optimizing nesting across compatible products</p>
            </div>

            {/* Sheet Settings */}
            <div className="bg-card border border-border rounded-lg" data-testid="sheet-settings-section">
                <button
                    className="w-full px-5 py-3.5 flex items-center justify-between text-left"
                    onClick={() => setSettingsOpen(prev => !prev)}
                    data-testid="toggle-sheet-settings"
                >
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                        <GearSix size={16} />
                        Sheet Settings
                        {!isDefaultSettings && (
                            <Badge variant="secondary" className="text-[10px]">Custom</Badge>
                        )}
                    </h3>
                    <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground font-mono">
                            {sheetLength} x {sheetWidth} mm | Margin: {edgeMargin} mm | Gap: {cuttingGap} mm | Tol: {tolerance} mm
                        </span>
                        {settingsOpen ? <CaretUp size={14} /> : <CaretDown size={14} />}
                    </div>
                </button>

                {settingsOpen && (
                    <div className="px-5 pb-4 border-t border-border pt-4 space-y-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                            <div>
                                <Label htmlFor="s-length" className="text-xs font-medium">Sheet Length (mm)</Label>
                                <Input id="s-length" type="number" value={sheetLength} onChange={e => setSheetLength(e.target.value)}
                                    className="h-9 mt-1 font-mono" data-testid="setting-sheet-length" />
                                <p className="text-[10px] text-muted-foreground mt-0.5">Default: 2440 (8 ft)</p>
                            </div>
                            <div>
                                <Label htmlFor="s-width" className="text-xs font-medium">Sheet Width (mm)</Label>
                                <Input id="s-width" type="number" value={sheetWidth} onChange={e => setSheetWidth(e.target.value)}
                                    className="h-9 mt-1 font-mono" data-testid="setting-sheet-width" />
                                <p className="text-[10px] text-muted-foreground mt-0.5">Default: 1220 (4 ft)</p>
                            </div>
                            <div>
                                <Label htmlFor="s-margin" className="text-xs font-medium">Edge Margin (mm)</Label>
                                <Input id="s-margin" type="number" value={edgeMargin} onChange={e => setEdgeMargin(e.target.value)}
                                    className="h-9 mt-1 font-mono" data-testid="setting-edge-margin" />
                                <p className="text-[10px] text-muted-foreground mt-0.5">Board edge to first piece</p>
                            </div>
                            <div>
                                <Label htmlFor="s-gap" className="text-xs font-medium">Cutting Gap (mm)</Label>
                                <Input id="s-gap" type="number" value={cuttingGap} onChange={e => setCuttingGap(e.target.value)}
                                    className="h-9 mt-1 font-mono" data-testid="setting-cutting-gap" />
                                <p className="text-[10px] text-muted-foreground mt-0.5">Piece-to-piece gap</p>
                            </div>
                            <div>
                                <Label htmlFor="s-tol" className="text-xs font-medium">Extra Tolerance (mm)</Label>
                                <Input id="s-tol" type="number" value={tolerance} onChange={e => setTolerance(e.target.value)}
                                    className="h-9 mt-1 font-mono" data-testid="setting-tolerance" />
                                <p className="text-[10px] text-muted-foreground mt-0.5">Per-side oversize (if enabled)</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={resetSettings} disabled={isDefaultSettings} data-testid="use-defaults-btn">
                                <ArrowCounterClockwise size={14} className="mr-1" />Use Defaults
                            </Button>
                            {!isDefaultSettings && (
                                <span className="text-xs text-amber-600">Custom settings active</span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Parameters */}
            <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-sm">Options</h3>
                </div>
                <div className="flex items-center gap-6 flex-wrap">
                    <div className="flex items-center gap-2">
                        <Switch id="oversize" checked={allowOversize} onCheckedChange={setAllowOversize} data-testid="toggle-oversize" />
                        <Label htmlFor="oversize" className="text-xs">Allow +{tolerance} mm oversize tolerance per side</Label>
                    </div>
                    <div className="flex items-center gap-2">
                        <Switch id="overprod" checked={allowOverprod} onCheckedChange={setAllowOverprod} data-testid="toggle-overproduction" />
                        <Label htmlFor="overprod" className="text-xs">Allow overproduction to fill sheets</Label>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                        <Badge variant="outline" className="text-[10px] gap-1" data-testid="grain-count-badge">
                            <Lock size={10} />{grainSettings.length} grain-locked decor{grainSettings.length !== 1 ? 's' : ''}
                        </Badge>
                        {isAdmin() && (
                            <Button variant="outline" size="sm" onClick={() => setGrainDialogOpen(true)} data-testid="grain-settings-btn">
                                <GearSix size={14} className="mr-1" />Grain Settings
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Input Table */}
            <div className="bg-card border border-border rounded-lg">
                <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
                    <h3 className="font-semibold text-sm">Input Items</h3>
                    <div className="flex gap-2">
                        <ProductSearch onSelect={handleProductSelect} />
                        <Button variant="outline" size="sm" onClick={addRow} data-testid="add-row-btn">
                            <Plus size={14} className="mr-1" />Row
                        </Button>
                    </div>
                </div>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-8">#</TableHead>
                            <TableHead>SKU</TableHead>
                            <TableHead>Decor</TableHead>
                            <TableHead>Thickness (mm)</TableHead>
                            <TableHead>Length (mm)</TableHead>
                            <TableHead>Width (mm)</TableHead>
                            <TableHead>Qty</TableHead>
                            <TableHead>Orient</TableHead>
                            <TableHead className="w-10"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((row, idx) => (
                            <TableRow key={row.id} data-testid={`input-row-${idx}`}>
                                <TableCell className="text-xs text-muted-foreground">{idx + 1}</TableCell>
                                <TableCell>
                                    <Input value={row.sku} onChange={e => updateRow(row.id, 'sku', e.target.value)} className="h-8 text-xs font-mono" placeholder="CB01-1" data-testid={`input-sku-${idx}`} />
                                </TableCell>
                                <TableCell>
                                    <Input value={row.decor} onChange={e => updateRow(row.id, 'decor', e.target.value)} className="h-8 text-xs w-20" placeholder="959" data-testid={`input-decor-${idx}`} />
                                </TableCell>
                                <TableCell>
                                    <Input value={row.thickness} onChange={e => updateRow(row.id, 'thickness', e.target.value)} className="h-8 text-xs w-20" placeholder="4" data-testid={`input-thickness-${idx}`} />
                                </TableCell>
                                <TableCell>
                                    <Input type="number" value={row.length} onChange={e => updateRow(row.id, 'length', e.target.value)} className="h-8 text-xs w-24" placeholder="150" data-testid={`input-length-${idx}`} />
                                </TableCell>
                                <TableCell>
                                    <Input type="number" value={row.width} onChange={e => updateRow(row.id, 'width', e.target.value)} className="h-8 text-xs w-24" placeholder="230" data-testid={`input-width-${idx}`} />
                                </TableCell>
                                <TableCell>
                                    <Input type="number" min={1} value={row.quantity} onChange={e => updateRow(row.id, 'quantity', e.target.value)} className="h-8 text-xs w-20" data-testid={`input-qty-${idx}`} />
                                </TableCell>
                                <TableCell>
                                    <select
                                        value={row.orientation || 'auto'}
                                        onChange={e => updateRow(row.id, 'orientation', e.target.value)}
                                        className="h-8 text-xs rounded border border-input bg-background px-1.5 w-[90px]"
                                        data-testid={`input-orient-${idx}`}
                                    >
                                        {ORIENTATION_OPTIONS.map(o => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </TableCell>
                                <TableCell>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => removeRow(row.id)} data-testid={`remove-row-${idx}`}>
                                        <Trash size={14} />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <div className="px-5 py-3 border-t border-border flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                        {rows.filter(r => r.sku && r.length && r.width).length} valid item(s) |
                        Products with same decor + thickness are grouped together
                    </p>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={handleReset} data-testid="reset-btn">
                            <ArrowsClockwise size={14} className="mr-1" />Reset
                        </Button>
                        <Button size="sm" onClick={handleOptimize} disabled={loading} data-testid="optimize-btn">
                            <Calculator size={16} className="mr-1" />{loading ? 'Calculating...' : 'Optimize'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Assumptions Panel */}
            {results?.assumptions && <AssumptionsPanel assumptions={results.assumptions} />}

            {/* Results */}
            {results && results.groups.map((group, gi) => (
                <div key={gi} className="space-y-4" data-testid={`result-group-${gi}`}>
                    <div className="bg-card border border-border rounded-lg">
                        <div className="px-5 py-3.5 border-b border-border">
                            <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-sm flex items-center gap-2">
                                    Decor {group.decor} | {group.thickness} mm
                                    {group.grain_locked && (
                                        <Badge variant="default" className="text-[10px] gap-1 bg-amber-600" data-testid={`grain-badge-${gi}`}>
                                            <Lock size={10} />Grain Locked
                                        </Badge>
                                    )}
                                </h3>
                                <div className="flex items-center gap-3">
                                    {group.strategy && (
                                        <Badge variant="outline" className="text-[10px] font-mono" data-testid={`strategy-${gi}`}>
                                            {group.strategy}
                                        </Badge>
                                    )}
                                    <Badge variant="outline" className="text-xs font-mono" data-testid={`total-sheets-${gi}`}>
                                        {group.total_sheets} sheet{group.total_sheets !== 1 ? 's' : ''}
                                    </Badge>
                                    <Badge
                                        variant={group.overall_efficiency_pct >= 70 ? 'default' : 'secondary'}
                                        className="text-xs"
                                        data-testid={`efficiency-${gi}`}
                                    >
                                        {group.overall_efficiency_pct}% efficiency
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">{group.scrap_pct}% scrap</span>
                                </div>
                            </div>
                        </div>

                        <div className="p-4">
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Pieces Produced</h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                                {Object.entries(group.sku_summary).map(([sku, info]) => (
                                    <div key={sku} className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted/40 border border-border/40">
                                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: skuColorMap[sku] || '#999' }} />
                                        <div className="min-w-0">
                                            <div className="text-xs font-mono font-semibold truncate">{sku}</div>
                                            <div className="text-[10px] text-muted-foreground">
                                                {info.produced}/{info.required}
                                                {info.produced >= info.required
                                                    ? <CheckCircle size={10} className="inline ml-1 text-green-600" weight="fill" />
                                                    : <WarningCircle size={10} className="inline ml-1 text-amber-500" weight="fill" />
                                                }
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {Object.keys(group.unfit_pieces).length > 0 && (
                                <div className="mt-3 p-2 bg-destructive/10 border border-destructive/20 rounded text-xs text-destructive">
                                    <WarningCircle size={14} className="inline mr-1" />
                                    Could not fit: {Object.entries(group.unfit_pieces).map(([sku, cnt]) => `${sku} x${cnt}`).join(', ')}
                                </div>
                            )}
                        </div>

                        <div className="px-4 pb-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div className="bg-muted/30 rounded-md p-3 text-center">
                                <div className="text-2xl font-bold" data-testid={`stat-sheets-${gi}`}>{group.total_sheets}</div>
                                <div className="text-[10px] text-muted-foreground uppercase">Sheets Required</div>
                            </div>
                            <div className="bg-muted/30 rounded-md p-3 text-center">
                                <div className="text-2xl font-bold">{group.total_pieces_placed}</div>
                                <div className="text-[10px] text-muted-foreground uppercase">Pieces Placed</div>
                            </div>
                            <div className="bg-muted/30 rounded-md p-3 text-center">
                                <div className="text-2xl font-bold">{group.overall_efficiency_pct}%</div>
                                <div className="text-[10px] text-muted-foreground uppercase">Layout Efficiency</div>
                            </div>
                            <div className="bg-muted/30 rounded-md p-3 text-center">
                                <div className="text-2xl font-bold">{group.scrap_pct}%</div>
                                <div className="text-[10px] text-muted-foreground uppercase">Scrap / Waste</div>
                            </div>
                        </div>
                    </div>

                    {/* Per-Sheet Detail */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {group.sheets.map((sheet) => (
                            <div key={sheet.sheet_number} className="bg-card border border-border rounded-lg p-4" data-testid={`sheet-card-${gi}-${sheet.sheet_number}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-semibold text-sm">Sheet #{sheet.sheet_number}</h4>
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                        <span>{sheet.pieces_count} piece{sheet.pieces_count !== 1 ? 's' : ''}</span>
                                        <Badge variant="outline" className="text-[10px]">{sheet.efficiency_pct}% used</Badge>
                                    </div>
                                </div>

                                <div className="flex justify-center mb-3 pl-5">
                                    <SheetVisual sheet={sheet} sheetW={visW} sheetH={visH} skuColorMap={skuColorMap} />
                                </div>

                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="text-[10px] py-1">SKU</TableHead>
                                            <TableHead className="text-[10px] py-1">Position</TableHead>
                                            <TableHead className="text-[10px] py-1">Size (mm)</TableHead>
                                            <TableHead className="text-[10px] py-1">Rotated</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {sheet.placements.map((p, pi) => (
                                            <TableRow key={pi}>
                                                <TableCell className="py-1">
                                                    <div className="flex items-center gap-1.5">
                                                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: skuColorMap[p.sku] || '#999' }} />
                                                        <span className="text-[11px] font-mono font-medium">{p.sku}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-[11px] font-mono py-1">({Math.round(p.x)}, {Math.round(p.y)})</TableCell>
                                                <TableCell className="text-[11px] font-mono py-1">{Math.round(p.w)} x {Math.round(p.h)}</TableCell>
                                                <TableCell className="text-[11px] py-1">{p.rotated ? 'Yes' : '\u2014'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>

                                {sheet.leftover_areas.length > 0 && (
                                    <div className="mt-2 text-[10px] text-muted-foreground">
                                        Leftover: {sheet.leftover_areas.map((a, i) => (
                                            <span key={i} className="inline-block mr-2">
                                                {a.note ? `~${(a.area_mm2 / 1000000).toFixed(3)} m\u00B2 (${a.note})` : `${a.w}x${a.h} mm (${(a.area_mm2 / 1000000).toFixed(3)} m\u00B2)`}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {/* Grain Settings Dialog */}
            <Dialog open={grainDialogOpen} onOpenChange={setGrainDialogOpen}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Grain Direction Settings</DialogTitle>
                        <DialogDescription>
                            Wood decors with grain-sensitive patterns must not be rotated during sheet optimization. Manage the grain-locked decor list below.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="flex gap-2">
                            <Input value={newGrainDecor} onChange={e => setNewGrainDecor(e.target.value)} placeholder="Decor # (e.g. 959)" className="w-32 text-sm" data-testid="grain-decor-input" />
                            <Input value={newGrainLabel} onChange={e => setNewGrainLabel(e.target.value)} placeholder="Label (e.g. Oak Wood)" className="flex-1 text-sm" data-testid="grain-label-input" />
                            <Button size="sm" onClick={handleAddGrain} data-testid="grain-add-btn">
                                <Plus size={14} className="mr-1" />Add
                            </Button>
                        </div>
                        {grainSettings.length === 0 ? (
                            <div className="text-center py-8 text-sm text-muted-foreground">
                                <LockOpen size={24} className="mx-auto mb-2 opacity-40" />
                                No grain-locked decors configured. All decors allow rotation.
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Decor</TableHead>
                                        <TableHead>Label</TableHead>
                                        <TableHead className="w-10"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {grainSettings.map(g => (
                                        <TableRow key={g.decor} data-testid={`grain-row-${g.decor}`}>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5">
                                                    <Lock size={12} className="text-amber-600" />
                                                    <span className="font-mono font-semibold text-sm">{g.decor}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">{g.label || '\u2014'}</TableCell>
                                            <TableCell>
                                                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleRemoveGrain(g.decor)} data-testid={`grain-remove-${g.decor}`}>
                                                    <Trash size={14} />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
